import { disputesAccessOverrides } from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import { LabelExpirationStrategy } from "@redotech/redo-return-core-common/label-expiration-strategy";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { PredefinedChipOptions } from "../customer-widget/customer-widget";
import { CompensationMethod } from "../return";
import { ManagedUserRoles } from "../roles";
import type {
  SettingGroupDefinition,
  SettingMetadata,
  SettingProductArea,
} from "../settings-catalog/types";
import { OrderEditingFilterSchema, OrderEditingRuleSchema } from "../team";
import { createPrefixedSchema, ISODateString } from "./schema-utils";
import {
  SupportFlatTeamSettingCatalogDefinitions,
  SupportFlatTeamSettingMetadataByKey,
} from "./support/catalog";
import { SupportFlatTeamSettingGroups } from "./support/groups";
import { SupportFlatTeamSettingsSchema } from "./support/settings";

export type NOOP<T> = { [K in keyof T]: T[K] } & {};

/**
 * Flat Zod schema for team settings based on the flattened structure.
 * This schema represents settings paths like "support/ai/enabled" and "concierge/general/name"
 * as flat keys, eliminating the need for complex nested object updates.
 */

// Note: Removed non-customer experience schemas (loyalty, marketing, returns, etc.)
// Keeping only customer experience and AI related settings. Other ones can be ported over as needed.

// Base types and enums
const ResponseLength = z.string();

const AccountTabSectionType = createEnum([
  "wish_list",
  "recent_orders",
  "loyalty_points",
  "viewed_products",
  "product_recommendations",
]);
const ConciergeSettingsSchema_ = z.object({
  ...createPrefixedSchema("concierge/general", {
    name: z.string().nullish(),
    toneOfVoice: z.string().nullish(),
    brandDescription: z.string().nullish(),
    customerDescription: z.string().nullish(),
    outputLength: ResponseLength.nullish(),
    welcomeMessage: z.string().nullish(),
    talkingPointsKnowledgeBase: z.string().nullish(),
    enableAiWelcomeMessage: z.boolean().nullish(),
    enableTalkingPointsKb: z.boolean().nullish(),
    suggestedResponsesEnabled: z.boolean().nullish(),
    writingExamples: z.array(z.any()).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/general/channelDescriptions", {
    email: z.string().nullish(),
    live: z.string().nullish(),
    sms: z.string().nullish(),
    social: z.string().nullish(),
  }).shape,
  "concierge/enabled": z.boolean().nullish(),
  "concierge/excludedProductTags": z.array(z.string()).nullish(),
  "concierge/excludedCollections": z.array(z.string()).nullish(),
  "concierge/excludedCategoryPath": z.array(z.string()).nullish(),
  "concierge/overriddenBestSellers": z.array(z.string()).nullish(),
  "concierge/context/productMetafieldKeys": z.array(z.string()).nullish(),
  "concierge/placeProductFormFromThemeExtension": z.boolean().nullish(),
  "concierge/themeExtensionProductFormPlaceholder": z.string().nullish(),
  "concierge/themeExtensionProductFormButtonText": z.string().nullish(),
  "concierge/themeExtensionProductFormFontSize": z.string().nullish(),
  "concierge/themeExtensionProductFormBorderRadius": z.string().nullish(),
  "concierge/themeExtensionProductFormButtonBackgroundColor": z
    .string()
    .nullish(),
  "concierge/themeExtensionProductFormButtonTextColor": z.string().nullish(),
  "concierge/themeExtensionProductFormCustomCss": z.string().nullish(),
  "concierge/proactiveChipEngagement": z.boolean().nullish(),
  "concierge/questionPromptCount": z.number().int().min(0).nullish(),
  ...createPrefixedSchema("concierge/skills/backInStockNotifications", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/cancelOrder", {
    enabled: z.boolean().nullish(),
    cancelOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/editAddress", {
    enabled: z.boolean().nullish(),
    updateOrderAddressTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/editOrderQuantity", {
    enabled: z.boolean().nullish(),
    editOrderQuantityTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/orderTracking", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/cartEditing", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productBundle", {
    enabled: z.boolean().nullish(),
    maxDiscountPercentage: z.number().min(0).max(10).nullish(),
    discountCodeExpirationMinutes: z.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/createDiscountCode", {
    enabled: z.boolean().nullish(),
    maxDiscountPercentage: z.number().min(0).nullish(),
    discountCodeExpirationMinutes: z.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/updateOrder", {
    enabled: z.boolean().nullish(),
    updateOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/webScrapingForProductInformation", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/forceUseRetrieveSupportKnowledge", {
    enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productVisualization", {
    enabled: z.boolean().nullish(),
    tryItOnMessage: z.string().max(500).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/skills/productRecommendations", {
    useShopifyRecommendations: z.boolean().nullish(),
  }).shape,
  "concierge/skills/returnsPortalLinkOverride/url": z.string().nullish(),
  ...createPrefixedSchema("concierge/emailSignUp", {
    buttonLabel: z.string().nullish(),
    incentivize: z.boolean().nullish(),
    signUpDiscountAmount: z.coerce.number().min(0).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/narrowProductRecommendation", {
    enabled: z.boolean().nullish(),
    productCount: z.number().int().min(1).nullish(),
  }).shape,
  ...createPrefixedSchema("concierge/guardrails", {
    storeContactInformation: z.string().nullish(),
  }).shape,
});

const CustomerWidgetSettingsSchema_ = z.object({
  ...createPrefixedSchema("customerWidget/general", {
    widgetColor: z.string().nullish(),
    widgetSecondaryColor: z.string().nullish(),
    widgetSecondaryColorEnabled: z.boolean().nullish(),
    hideButton: z.boolean().nullish(),
    widgetPosition: createEnum([
      "bottom-right",
      "bottom-left",
      "left",
      "right",
    ]).nullish(),
    widgetButtonFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetHeaderFontSize: z.coerce.number().int().min(6).max(72).nullish(),
    widgetMessageBubbleFontSize: z.coerce
      .number()
      .int()
      .min(6)
      .max(24)
      .nullish(),
    widgetQuickActionFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetQuickLinkFontSize: z.coerce.number().int().min(6).max(24).nullish(),
    widgetFontFamily: z.string().nullish(),
    headingLogoUrl: z.url().nullish(),
    mainHeadingText: z.string().nullish(),
    homeHeadingText: z.string().nullish(),
    bubbleButtonText: z.string().nullish(),
    chatWithUsButtonText: z.string().nullish(),
    customCss: z.string().nullish(),
    enableInAppEmbed: z.boolean().nullish(),
    openToWelcomePage: z.boolean().nullish(),
    mobileFullscreen: z.boolean().nullish(),
    showCustomerWidgetInReturnApp: z.boolean().nullish(),
    xAdjustment: z.coerce.number().nullish(),
    yAdjustment: z.coerce.number().nullish(),
    mobileXAdjustment: z.coerce.number().nullish(),
    mobileYAdjustment: z.coerce.number().nullish(),
    zIndex: z.coerce.number().int().min(0).nullish(),
    mobileZIndex: z.coerce.number().int().min(0).nullish(),
    hideWidgetOnProductPages: z.boolean().nullish(),
    customerWidgetV2Enabled: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/gradientSetting", {
    gradientStrength: z.number().min(0).max(1).nullish(),
    inverted: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/chips", {
    customChips: z.array(z.string()).nullish(),
    selectedPredefinedChips: z
      .array(z.nativeEnum(PredefinedChipOptions))
      .nullish(),
  }).shape,
  ...createPrefixedSchema("customerWidget/general/selfServe", {
    editOrderLabel: z.string().nullish(),
    startClaimEnabled: z.boolean().nullish(),
    startReturnEnabled: z.boolean().nullish(),
    startReturnLabel: z.string().nullish(),
    trackPackageEnabled: z.boolean().nullish(),
    trackPackageSettingLabel: z.string().nullish(),
  }).shape,
  "customerWidget/general/widgetQuickLinks": z
    .array(z.object({ title: z.string(), url: z.url() }))
    .nullish(),

  "customerWidget/customerAccounts/accountTabSections": z
    .array(
      z.object({
        type: AccountTabSectionType,
        header: z.string(),
        enabled: z.boolean(),
      }),
    )
    .nullish(),
  "customerWidget/customerAccounts/registerHeader": z.string().nullish(),
  "customerWidget/customerAccounts/registerSubtext": z.string().nullish(),
  "customerWidget/customerAccounts/showAccountsPage": z.boolean().nullish(),
});

const AiAutopilotSettingsSchema_ = createPrefixedSchema("ai/autopilot", {
  enabled: z.boolean().nullish(),
  maxDailyTickets: z.number().int().min(0).nullish(),
  confidenceThreshold: z.number().min(0).max(1).nullish(),
  escalationKeywords: z.array(z.string()).nullish(),
  autoCloseEnabled: z.boolean().nullish(),
  autoCloseDelayHours: z.number().int().min(1).nullish(),
  approvalRequired: z.boolean().nullish(),
  approverEmails: z.array(z.email()).nullish(),
  enabledChannels: z.array(z.string()).nullish(),
});

const KnowledgeBaseSettingsSchema_ = createPrefixedSchema("knowledgeBase", {
  enabled: z.boolean().nullish(),
  autoUpdate: z.boolean().nullish(),
  sources: z
    .array(
      z.object({
        type: createEnum(["url", "file", "text"]),
        content: z.string(),
        title: z.string().nullish(),
        isActive: z.boolean(),
      }),
    )
    .nullish(),
  lastUpdated: ISODateString.nullish(),
  indexingEnabled: z.boolean().nullish(),
});

import { OrderEditingDisallowEventZod } from "../order-editing-disallow-event";

export enum HoldStatusStrategy {
  SHOPIFY_FULFILLMENT_HOLD = "shopify_fulfillment_hold",
  SHOPIFY_ORDER_TAG = "shopify_order_tag",
}

export const HoldStatusStrategyZod = createEnum([
  HoldStatusStrategy.SHOPIFY_FULFILLMENT_HOLD,
  HoldStatusStrategy.SHOPIFY_ORDER_TAG,
]);

const OrderEditingSettingsSchema_ = z.object({
  "orderEditing/useOmsEvents": z.boolean().nullish(),
  ...createPrefixedSchema("orderEditing/holds", {
    applyHoldToAllOrders: z.boolean().nullish(),
    holdDurationInMinutes: z.number().int().min(0).nullish(),
    holdOrdersRequiringPayment: z.boolean().nullish(),
    holdStatusStrategy: HoldStatusStrategyZod.nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/cancelOrder", {
    enabled: z.boolean().nullish(),
    cancelOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
    requireConfirmation: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/changeAddress", {
    enabled: z.boolean().nullish(),
    changeOrderAddressTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
    requireConfirmation: z.boolean().nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/addOrderDiscount", {
    enabled: z.boolean().nullish(),
    addOrderDiscountTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/addOrderGiftNote", {
    enabled: z.boolean().nullish(),
    addOrderGiftNoteTimeWindowAfterPlacingMinutes: z
      .number()
      .int()
      .min(0)
      .nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/tools/updateOrder", {
    enabled: z.boolean().nullish(),
    updateOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0).nullish(),
    addItem: z.boolean().nullish(),
    removeItem: z.boolean().nullish(),
    changeVariant: z.boolean().nullish(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).nullish(),
  }).shape,
  ...createPrefixedSchema("orderEditing/enabledPlatforms", {
    shopperAI: z.boolean().nullish(),
    supportSelfServe: z.boolean().nullish(),
    customerPortal: z.boolean().nullish(),
    orderTracking: z.boolean().nullish(),
  }).shape,
  "orderEditing/filters": z.array(OrderEditingFilterSchema).nullish(),
  "orderEditing/rules": z.array(OrderEditingRuleSchema).nullish(),
});

const LocalizationSettingsSchema_ = createPrefixedSchema("localization", {
  enabled: z.boolean().nullish(),
});

const DisputesSettingsSchema_ = createPrefixedSchema("disputes", {
  enabled: z.boolean().nullish(),
  automateEvidenceEnabled: z.boolean().nullish(),
  accessOverride: createEnum(disputesAccessOverrides).nullish(),
  freeAccessEnabled: z.boolean().nullish(),
  "pricingOverride/successFeePercentage": z.number().min(0).max(100).nullish(),
  "pricingOverride/minFeeDollars": z.number().min(0).nullish(),
  "pricingOverride/source": z.string().nullish(),
  "pricingOverride/appliedAt": z.coerce.date().nullish(),
  reclaimShopifyAppInstallPromptDeferredUntil: ISODateString.nullish(),
  evidenceWindowDays: z.number().int().min(5).max(30).nullish(),
});

const LandedCostFlatSettingsSchema_ = createPrefixedSchema("landedCost", {
  enabled: z.boolean().nullish(),
  landedCostFee: z.number().nullish(),
  purchaseLabelsOutsideOms: z.boolean().nullish(),
});

const LcgCommitBillingFlatSettingsSchema_ = createPrefixedSchema(
  "lcgCommitBilling",
  {
    enabled: z.boolean().nullish(),
    startDate: z.coerce.date().nullish(),
    paymentProvider: z.string().nullish(),
    billingPeriod: z.literal("week").nullish(),
    renewOnPeriodEnd: z.boolean().nullish(),
  },
);

const ReturnsFraudFlatSettingsSchema_ = createPrefixedSchema(
  "returns/fraud/zipcodes",
  {
    flaggedZips: z.array(z.string()).nullish(),
    allowlistedZips: z.array(z.string()).nullish(),
    returnRateThresholdPercent: z.coerce.number().min(0).nullish(),
    minimumOrderAmount: z.coerce.number().min(0).nullish(),
  },
);

// Return shipping label expiration settings. Maps to settings.labelExpiration
// on the Team document — the "Label expiration" card under Settings >
// Automations, including the "Reopen return when expired label is used" toggle.
const LabelExpirationFlatSettingsSchema_ = createPrefixedSchema(
  "labelExpiration",
  {
    days: z.number().int().nullish(),
    strategy: z.nativeEnum(LabelExpirationStrategy).nullish(),
    reminderEmails: z.boolean().nullish(),
    reminderEmailDays: z.array(z.number().int()).nullish(),
    compensationMethodsToRemind: z
      .array(z.nativeEnum(CompensationMethod))
      .nullish(),
    expirationEmails: z.boolean().nullish(),
    shouldReopenReturnWhenLabelUsed: z.boolean().nullish(),
    reopenReturnAfterDays: z.number().int().nullish(),
    flagReturnWhenReopened: z.boolean().nullish(),
  },
);

const ContextualSettingsSchema_ = createPrefixedSchema("contextual", {
  enabled: z.boolean().nullish(),
  visible: z.boolean().nullish(),
  contextualMerchantId: z.string().nullish(),
  ctxDomain: z.string().nullish(),
  cloudflareHostnameId: z.string().nullish(),
  onboardingCompleted: z.boolean().nullish(),
  gtmContainerId: z.string().nullish(),
  pixelVerified: z.boolean().nullish(),
});

const NewUserDefaultsSettingsSchema_ = createPrefixedSchema("newUserDefaults", {
  managedRole: createEnum(ManagedUserRoles).nullish(),
  customRoleId: zExt.objectId().nullish(),
});

export const FlatTeamSettingsSchema = z.object({
  ...SupportFlatTeamSettingsSchema.shape,
  ...ConciergeSettingsSchema_.shape,
  ...CustomerWidgetSettingsSchema_.shape,
  ...AiAutopilotSettingsSchema_.shape,
  ...KnowledgeBaseSettingsSchema_.shape,
  ...OrderEditingSettingsSchema_.shape,
  ...LocalizationSettingsSchema_.shape,
  ...DisputesSettingsSchema_.shape,
  ...LandedCostFlatSettingsSchema_.shape,
  ...LcgCommitBillingFlatSettingsSchema_.shape,
  ...ContextualSettingsSchema_.shape,
  ...LabelExpirationFlatSettingsSchema_.shape,
  ...ReturnsFraudFlatSettingsSchema_.shape,
  ...NewUserDefaultsSettingsSchema_.shape,
});

export type FlatTeamSettings = z.infer<typeof FlatTeamSettingsSchema>;

export const FlatTeamSettingMetadataByKey =
  SupportFlatTeamSettingMetadataByKey as Partial<
    Record<keyof FlatTeamSettings, SettingMetadata>
  >;

export const FlatTeamSettingGroups = [
  ...SupportFlatTeamSettingGroups,
] as const satisfies readonly SettingGroupDefinition[];

export function getFlatTeamSettingMetadata(
  key: keyof FlatTeamSettings,
): SettingMetadata | undefined {
  return FlatTeamSettingMetadataByKey[key];
}

export function getFlatTeamSettingGroup(
  groupId: string,
): SettingGroupDefinition | undefined {
  return FlatTeamSettingGroups.find((group) => group.id === groupId);
}

export function getFlatTeamSettingKeysForGroup(
  groupId: string,
): (keyof FlatTeamSettings)[] {
  return SupportFlatTeamSettingCatalogDefinitions.filter((definition) => {
    const metadata = definition.metadata;
    return (
      definition.groupId === groupId ||
      metadata.relatedGroups?.includes(groupId)
    );
  }).map((definition) => definition.id as keyof FlatTeamSettings);
}

export function getFlatTeamSettingGroupsForProductArea(
  productArea: SettingProductArea,
): readonly SettingGroupDefinition[] {
  return FlatTeamSettingGroups.filter(
    (group) => group.productArea === productArea,
  );
}

export const FlatTeamSettingKeys = Object.keys(
  FlatTeamSettingsSchema.shape,
) as (keyof FlatTeamSettings)[];

export const ValidFlatSettingKeysSchema = z.array(
  createEnum(
    FlatTeamSettingKeys as [
      keyof FlatTeamSettings,
      ...(keyof FlatTeamSettings)[],
    ],
  ),
);

/**
 * Utility function to create a schema for a subset of keys
 */
export function createPickedFlatSettingsSchema<
  K extends keyof FlatTeamSettings,
>(keys: readonly K[]): z.ZodType<Pick<FlatTeamSettings, K>> {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const key of keys) {
    const keySchema = FlatTeamSettingsSchema.shape[key];
    if (keySchema) {
      schemaShape[key] = keySchema;
    } else {
      throw new Error(`Unknown setting key: ${key}`);
    }
  }

  return z.object(schemaShape) as unknown as z.ZodType<
    Pick<FlatTeamSettings, K>
  >;
}

const UpdateFlatTeamSettingsSchema = zExt.optionalToNullable(
  FlatTeamSettingsSchema,
);
export type UpdateFlatTeamSettings = z.infer<
  typeof UpdateFlatTeamSettingsSchema
>;

export function createPickedUpdateFlatSettingsSchema<
  K extends keyof FlatTeamSettings,
>(keys: readonly K[]): z.ZodType<Pick<UpdateFlatTeamSettings, K>> {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const key of keys) {
    const keySchema = UpdateFlatTeamSettingsSchema.shape[key];
    if (keySchema) {
      schemaShape[key] = keySchema;
    } else {
      throw new Error(`Unknown setting key: ${key}`);
    }
  }

  return z.object(schemaShape) as unknown as z.ZodType<
    Pick<UpdateFlatTeamSettings, K>
  >;
}

// Type guard for checking if a key exists in the flat settings schema
export function isFlatSettingKey(key: string): key is keyof FlatTeamSettings {
  return key in FlatTeamSettingsSchema.shape;
}

/**
 * Utility to get the schema for a specific key
 */
export function getSchemaForKey(
  key: keyof FlatTeamSettings,
): z.ZodTypeAny | undefined {
  return FlatTeamSettingsSchema.shape[key];
}

export const FlatTeamSettingsPartialRecordSchema = z.partialRecord(
  z.keyof(FlatTeamSettingsSchema),
  z.literal(1),
);
export type FlatTeamSettingsPartialRecord = z.infer<
  typeof FlatTeamSettingsPartialRecordSchema
>;

export {
  OrderEditingDisallowEvent,
  OrderEditingDisallowEventZod,
} from "../order-editing-disallow-event";
